/**
 * Entries — the log, read back, filtered and corrected.
 *
 * The tracker's Entries screen, near enough line for line: a period at a time
 * with a pager, filters that narrow rather than navigate, one row per entry
 * grouped under its day, and a footer that splits the period three ways.
 *
 * A week is the default because that is what a timesheet is cut on; a month is
 * what an invoice is cut on, so it sits beside it, with quarter for the same
 * periods Reports offers. "All time" is the escape hatch, and it pairs with the
 * project and client filters to answer where the hours on a job went.
 *
 * The by-project and by-client breakdowns that used to sit at the top of this
 * page have moved to Reports, which is where the same two tables already lived.
 */

import { useEffect, useMemo, useState } from "react";

import { useToast } from "@/context/ToastContext";
import { PlayIcon } from "@/components/NavIcons";
import { MonthPicker, QuarterPicker, WeekPicker, dayKey, monthOffsetOf, quarterOffsetOf, weekOffsetOf } from "@/components/PeriodPicker";
import { useContinueEntry } from "@/hooks/useContinueEntry";
import { useAllItems, useProjects } from "@/hooks/useData";
import { useClients, useTimeEntries, useTimeMutations } from "@/hooks/useTime";
import { KIND_LABEL, type EntryKind } from "@/lib/billing";
import { appMode } from "@/lib/pocketbase";
import {
  clockRange, clock, deriveEntryLabel, entryDayKey, groupBy, hours, inBounds,
  breakdownText, entryKind, isUnbilled, periodBounds, projectName, toIsoLocal, totalsOf,
} from "@/lib/timeRollup";

/** The periods the pager steps through. "All time" is a separate toggle. */
export type EntrySpan = "week" | "month" | "quarter";
const SPANS: [EntrySpan, string][] = [["week", "Week"], ["month", "Month"], ["quarter", "Quarter"]];
const offsetOf: Record<EntrySpan, (d: Date) => number> = {
  week: weekOffsetOf, month: monthOffsetOf, quarter: quarterOffsetOf,
};
import type { Client, Project, TimeEntry } from "@/lib/types";

export function EntriesTab({
  onEditEntry, onAddEntry, initialProjectId, initialClientId, initialActivity, initialAllTime, initialSpan, initialOffset,
}: {
  onEditEntry: (entry: TimeEntry) => void;
  onAddEntry: () => void;
  /** Set when arriving from a figure in Reports — see the Time shell. */
  initialProjectId?: string;
  initialClientId?: string;
  initialActivity?: string;
  initialAllTime?: boolean;
  /** The period Reports was showing, so its figure and this list agree. */
  initialSpan?: EntrySpan;
  initialOffset?: number;
}) {
  const { data: entries = [] } = useTimeEntries();
  const { data: projects = [] } = useProjects();
  const { data: clients = [] } = useClients();
  const { data: items = [] } = useAllItems();
  const { updateEntry, deleteEntry } = useTimeMutations();
  const continueEntry = useContinueEntry();
  const toast = useToast();

  /**
   * Open on the week of the most recent entry, not on this one.
   *
   * Coming back on a Monday to an empty list tells you nothing; the tracker
   * opens where the work is and so does this.
   */
  const [span, setSpan] = useState<EntrySpan>(initialSpan ?? "week");
  const [offset, setOffset] = useState(initialOffset ?? 0);
  /**
   * Open on the period of the most recent entry, not on this one.
   *
   * This lived in useState's initialiser, which runs on the FIRST render —
   * before React Query has resolved, when `entries` is still empty. So it
   * always took the empty branch and always opened on the current week, which
   * on a Monday morning is a blank screen with your work one tap away and no
   * sign of it. Runs once, when the data actually arrives — and not at all
   * when Reports sent a period, which is the one being asked about.
   */
  const [homed, setHomed] = useState(initialSpan !== undefined);
  useEffect(() => {
    if (homed || entries.length === 0) return;
    const latest = entries.reduce((m, e) => (e.start > m ? e.start : m), entries[0].start);
    setHomed(true);
    setOffset(Math.min(0, offsetOf[span](new Date(latest))));
  }, [entries, homed, span]);
  // Arriving from Reports means "show me what is behind that number": the same
  // period it was showing, or all of it when it sent no period.
  const [allTime, setAllTime] = useState(Boolean(initialAllTime));
  const [projectId, setProjectId] = useState(initialProjectId ?? "all");
  const [clientId, setClientId] = useState(initialClientId ?? "all");
  const [activity, setActivity] = useState(initialActivity ?? "all");
  const [billableOnly, setBillableOnly] = useState(false);
  const [unbilledOnly, setUnbilledOnly] = useState(false);
  const [query, setQuery] = useState("");
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draftNote, setDraftNote] = useState("");

  const bounds = useMemo(() => periodBounds(span, offset), [span, offset]);
  const [picking, setPicking] = useState(false);
  /** Switching Week ↔ Month keeps your place: the month that holds the week. */
  function changeSpan(next: EntrySpan) {
    if (next === span) return;
    setOffset(offsetOf[next](bounds.start));
    setSpan(next);
    setAllTime(false);
    setPicking(false);
  }
  const pickTo = (d: Date) => { setAllTime(false); setOffset(offsetOf[span](d)); setPicking(false); };
  /** Days with time logged, for the dots in the period picker. */
  const loggedDays = useMemo(() => new Set(entries.map((e) => dayKey(new Date(e.start)))), [entries]);
  const itemTitle = useMemo(
    () => new Map(items.map((i) => [i.id, i.title])),
    [items],
  );

  /**
   * Clients that have time logged, from the data — the same link Reports'
   * "By client" groups on, so a filtered total here matches the row there.
   */
  const clientOptions = useMemo(() => {
    const seen = new Set(entries.map((e) => e.client).filter(Boolean) as string[]);
    return clients
      .filter((c) => seen.has(c.id) || c.id === clientId)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [entries, clients, clientId]);

  /** With a client picked, the project list narrows to that client's work. */
  const projectOptions = useMemo(() => {
    const pool = projects.filter((p) => p.kind !== "inbox");
    if (clientId === "all") return pool;
    const used = new Set(entries.filter((e) => e.client === clientId && e.project).map((e) => e.project));
    return pool.filter((p) => p.clientRef === clientId || used.has(p.id) || p.id === projectId);
  }, [projects, entries, clientId, projectId]);

  /** From the data, so an activity dropped from Settings can still be found. */
  const activityOptions = useMemo(() => {
    const seen = new Set(entries.map((e) => e.category).filter(Boolean) as string[]);
    return [...seen].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [entries]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (!allTime && !inBounds(e, bounds)) return false;
      if (projectId === "none" ? e.project : projectId !== "all" && e.project !== projectId) return false;
      if (clientId === "none" ? e.client : clientId !== "all" && e.client !== clientId) return false;
      if (activity === "none" ? e.category : activity !== "all" && e.category !== activity) return false;
      if (billableOnly && entryKind(e, projects) !== "hourly") return false;
      if (unbilledOnly && !isUnbilled(e, projects)) return false;
      if (!q) return true;
      return `${e.note ?? ""} ${e.titleSnapshot ?? ""} ${projectName(projects, e.project)}`.toLowerCase().includes(q);
    });
  }, [entries, allTime, bounds, projectId, clientId, activity, billableOnly, unbilledOnly, query, projects]);

  const totals = totalsOf(filtered, projects);
  const byDay = useMemo(() => {
    const map = groupBy(filtered, entryDayKey);
    return [...map.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([day, rows]) => ({
        day,
        rows: [...rows].sort((a, b) => (b.start || "").localeCompare(a.start || "")),
      }));
  }, [filtered]);

  function saveRename(entry: TimeEntry) {
    const next = draftNote.trim();
    setRenaming(null);
    if (next === (entry.note ?? "").trim()) return;
    void updateEntry.mutateAsync({ id: entry.id, note: next }).catch(() => toast("Could not rename that entry"));
  }

  function exportCsv() {
    if (filtered.length === 0) { toast("Nothing to export in this view"); return; }
    const csv = entriesToCsv(filtered, projects, clients, itemTitle);
    const name = `time-entries-${allTime ? "all-time" : bounds.label.replace(/[^\w]+/g, "-").toLowerCase()}.csv`;
    download(name, csv);
    toast(`Exported ${filtered.length} ${filtered.length === 1 ? "entry" : "entries"}`);
  }

  const step = (dir: -1 | 1, label: string) => (
    <button
      onClick={() => { setAllTime(false); setOffset((o) => o + dir); }}
      aria-label={label}
      style={{ ...pagerBtn, opacity: allTime ? 0.45 : 1 }}
    >{dir === -1 ? "‹" : "›"}</button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* -- Toolbar ---------------------------------------------------------- */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", padding: 3, background: "var(--tint)", borderRadius: 999 }}>
            {SPANS.map(([k, label]) => {
              const on = k === span && !allTime;
              return (
                <button key={k} onClick={() => changeSpan(k)} aria-pressed={on} style={{
                  padding: "6px 13px", borderRadius: 999, fontSize: 12.5, cursor: "pointer", border: "none",
                  background: on ? "var(--surface)" : "transparent",
                  color: on ? "var(--accent)" : "var(--ink3)",
                  fontWeight: on ? "var(--fw-title)" : "var(--fw-medium)",
                  boxShadow: on ? "var(--shadowCard)" : "none",
                }}>{label}</button>
              );
            })}
          </div>
          {step(-1, `Previous ${span}`)}
          <div style={{ position: "relative" }}>
            <button
              className="mono"
              onClick={() => setPicking((v) => !v)}
              // The picker closes on any outside mousedown; without this, a
              // click here would close it and the click would reopen it.
              onMouseDown={(e) => e.stopPropagation()}
              aria-haspopup="dialog"
              aria-expanded={picking}
              title={`Pick a ${span}`}
              style={{
                height: 36, minWidth: 156, padding: "0 12px", borderRadius: 8,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "1px solid var(--line2)", fontSize: 13, background: "var(--surface)", cursor: "pointer",
                color: allTime ? "var(--muted)" : "var(--ink)",
              }}
            >{allTime ? "All time" : bounds.label}</button>
            {picking && span === "week" && (
              <WeekPicker selected={allTime ? null : bounds.start} marked={loggedDays} onPick={pickTo} onClose={() => setPicking(false)} />
            )}
            {picking && span === "month" && (
              <MonthPicker selected={allTime ? null : bounds.start} marked={loggedDays} onPick={pickTo} onClose={() => setPicking(false)} />
            )}
            {picking && span === "quarter" && (
              <QuarterPicker selected={allTime ? null : bounds.start} marked={loggedDays} onPick={pickTo} onClose={() => setPicking(false)} />
            )}
          </div>
          {step(1, `Next ${span}`)}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            aria-label="Filter by project"
            style={{ ...pill, ...(projectId !== "all" ? pillOn : null), cursor: "pointer", maxWidth: 200 }}
          >
            <option value="all">All projects</option>
            <option value="none">Ad-hoc (no project)</option>
            {projectOptions.map((p) => (
              <option key={p.id} value={p.id}>{p.client ? `${p.name} · ${p.client}` : p.name}</option>
            ))}
          </select>

          <select
            value={clientId}
            onChange={(e) => {
              const next = e.target.value;
              setClientId(next);
              // A project from another client would leave the list empty with
              // no hint why; drop it rather than combine two filters that miss.
              if (next !== "all" && projectId !== "all" && projectId !== "none"
                && !entries.some((x) => x.project === projectId && (next === "none" ? !x.client : x.client === next))) {
                setProjectId("all");
              }
            }}
            aria-label="Filter by client"
            style={{ ...pill, ...(clientId !== "all" ? pillOn : null), cursor: "pointer", maxWidth: 180 }}
          >
            <option value="all">All clients</option>
            <option value="none">No client</option>
            {clientOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          {activityOptions.length > 0 && (
            <select
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              aria-label="Filter by activity"
              style={{ ...pill, ...(activity !== "all" ? pillOn : null), cursor: "pointer", maxWidth: 170 }}
            >
              <option value="all">All activities</option>
              <option value="none">No activity</option>
              {activityOptions.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          )}

          <button
            onClick={() => setBillableOnly((v) => !v)}
            style={{ ...pill, ...(billableOnly ? pillOn : null) }}
          >
            <span style={{ display: "inline-block", width: 11 }}>{billableOnly ? "✓" : ""}</span>Billable
          </button>

          <button
            onClick={() => setUnbilledOnly((v) => !v)}
            title="Billable work that has not gone out on an invoice yet"
            style={{ ...pill, ...(unbilledOnly ? pillOn : null) }}
          >
            <span style={{ display: "inline-block", width: 11 }}>{unbilledOnly ? "✓" : ""}</span>Not invoiced
          </button>

          <button onClick={() => setAllTime((v) => !v)} style={{ ...pill, ...(allTime ? pillOn : null) }}>
            {allTime ? "All time ✕" : "All time"}
          </button>

          <span style={{
            display: "inline-flex", alignItems: "center", gap: 7, height: 34, padding: "0 12px",
            borderRadius: 999, border: "1px solid var(--line2)", background: "var(--surface)",
          }}>
            <span style={{ color: "var(--muted2)", fontSize: 12 }}>⌕</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search"
              aria-label="Search entries"
              style={{ width: 130, border: "none", outline: "none", background: "transparent", fontSize: 12.5, color: "var(--ink)" }}
            />
          </span>

          <button onClick={exportCsv} style={pill}>Export</button>
          <button onClick={onAddEntry} style={{ ...pill, background: "var(--accent)", border: "1px solid var(--accent)", color: "var(--onAccent)", fontWeight: 600 }}>
            + Add
          </button>
        </div>
      </div>

      {/* -- Column header ---------------------------------------------------- */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "0 8px 8px", borderBottom: "1px solid var(--line)" }}>
        <span style={{ ...head, width: 46 }}>Dur</span>
        <span style={{ ...head, flex: 1 }}>Description</span>
        <span style={{ ...head, width: 190 }}>Project · task</span>
        <span style={{ ...head, width: 148, textAlign: "right" }}>Time</span>
        <span style={{ ...head, width: 52, textAlign: "right" }}>Bill</span>
        <span style={{ width: 24 }} />
        <span style={{ width: 22 }} />
      </div>

      {/* -- Days ------------------------------------------------------------- */}
      {byDay.length === 0 ? (
        <div style={{ padding: "48px 0", textAlign: "center", fontSize: 13.5, color: "var(--muted)" }}>
          No entries match these filters.
        </div>
      ) : (
        byDay.map(({ day, rows }) => {
          const t = totalsOf(rows, projects);
          return (
            <div key={day} style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "10px 8px 6px" }}>
                <span style={{ fontSize: 13, fontWeight: "var(--fw-title)", color: "var(--ink)" }}>{longDayLabel(day)}</span>
                <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
                  {clock(t.seconds)}{breakdownText(t, clock) ? ` · ${breakdownText(t, clock)}` : ""}
                </span>
              </div>

              {rows.map((entry) => {
                const project = projects.find((p) => p.id === entry.project);
                const editing = renaming === entry.id;
                return (
                  <div
                    key={entry.id}
                    onClick={() => { if (!editing) onEditEntry(entry); }}
                    className="item-row row-hover"
                    style={{
                      display: "flex", alignItems: "center", gap: 16, padding: "9px 8px", cursor: "pointer",
                      borderBottom: "1px solid var(--tint2)",
                      // Shaded: time that is not client work you're paid for.
                      background: ["internal", "no_charge"].includes(entryKind(entry, projects)) ? "var(--surface2)" : undefined,
                    }}
                  >
                    <span className="mono" style={{ flex: "none", width: 46, fontSize: 13.5, fontWeight: 500 }}>
                      {clock(entry.durationSeconds)}
                    </span>

                    <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ flex: "none", width: 8, height: 8, borderRadius: 2, background: project?.color ?? "var(--muted3)" }} />
                      {editing ? (
                        <input
                          autoFocus
                          value={draftNote}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setDraftNote(e.target.value)}
                          onBlur={() => saveRename(entry)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveRename(entry);
                            if (e.key === "Escape") setRenaming(null);
                          }}
                          aria-label="Description"
                          style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontSize: 15, color: "var(--ink)" }}
                        />
                      ) : (
                        /* Click the text to rename in place; click the row to
                           open the full editor. Renaming is the correction you
                           make ten times as often as any other. */
                        <button
                          onClick={(e) => { e.stopPropagation(); setRenaming(entry.id); setDraftNote(entry.note ?? ""); }}
                          title="Click to rename · click the row to open the editor"
                          style={{
                            flex: 1, minWidth: 0, textAlign: "left", border: "none", background: "none", padding: 0,
                            fontSize: 15, color: "var(--ink)", cursor: "text",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}
                        >{deriveEntryLabel(entry.note, itemTitle.get(entry.item ?? ""))}</button>
                      )}
                      {entry.travelPct ? (
                        <span title={`Travel — billed at ${entry.travelPct}% of the rate`} style={{
                          flex: "none", fontSize: 10.5, padding: "2px 6px", borderRadius: 999,
                          background: "var(--purpleTint)", color: "var(--purple)",
                        }}>travel {entry.travelPct}%</span>
                      ) : null}
                      {entry.orphaned && (
                        <span title="Its task was deleted in Todoist. The hours are kept." style={{
                          flex: "none", fontSize: 10.5, padding: "2px 6px", borderRadius: 999,
                          background: "var(--warnTint)", color: "var(--warn)",
                        }}>orphaned</span>
                      )}
                    </span>

                    <span style={{ flex: "none", width: 190, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <span style={{ color: "var(--ink2)" }}>{projectName(projects, entry.project)}</span>
                      {entry.item && itemTitle.get(entry.item) && (
                        <span style={{ color: "var(--muted)" }}> · {itemTitle.get(entry.item)}</span>
                      )}
                    </span>

                    <span className="mono" style={{ flex: "none", width: 148, textAlign: "right", fontSize: 12, color: "var(--muted)" }}>
                      {clockRange(entry)}
                    </span>

                    <span style={{ flex: "none", width: 52, textAlign: "right" }}>
                      {entry.invoicedAt
                        ? <InvoicedBadge entry={entry} />
                        : <KindMark kind={entryKind(entry, projects)} />}
                    </span>

                    {/* Continue: a timer on the same work, as in Toggl. Shown
                        on hover, like the task rows' timer controls. */}
                    <button
                      className="row-time-controls"
                      onClick={(e) => { e.stopPropagation(); void continueEntry(entry); }}
                      title="Continue — start a timer on this again"
                      aria-label="Continue this entry"
                      style={{
                        flex: "none", width: 24, height: 24, borderRadius: 999, border: "none", background: "none",
                        color: "var(--muted2)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
                      }}
                    ><PlayIcon size={13} /></button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (appMode === "demo") { toast("Not available in demo mode"); return; }
                        void deleteEntry.mutateAsync(entry.id);
                      }}
                      title="Delete entry"
                      style={{ flex: "none", width: 22, border: "none", background: "none", color: "var(--muted3)", cursor: "pointer", fontSize: 13 }}
                    >✕</button>
                  </div>
                );
              })}
            </div>
          );
        })
      )}

      {/* -- Footer ----------------------------------------------------------- */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 16, paddingTop: 14, borderTop: "1px solid var(--line)", flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>
          {span === "week" && !allTime ? "Week starts Monday · " : ""}{filtered.length} {filtered.length === 1 ? "entry" : "entries"}
        </span>
        <div style={{ display: "flex", alignItems: "baseline", gap: 20 }}>
          <Figure label="Billable" value={clock(totals.billableSeconds)} />
          <Figure label="Not invoiced" value={clock(totals.unbilledSeconds)} muted />
          {totals.projectSeconds > 0 && <Figure label="Project work" value={clock(totals.projectSeconds)} muted />}
          {totals.noChargeSeconds > 0 && <Figure label="No charge" value={clock(totals.noChargeSeconds)} muted />}
          {totals.internalSeconds > 0 && <Figure label="Internal" value={clock(totals.internalSeconds)} muted />}
          <Figure label="Total" value={clock(totals.seconds)} />
        </div>
      </div>
    </div>
  );
}

/** "Inv" with the date and number on hover — the row stays one line. */
export function InvoicedBadge({ entry }: { entry: TimeEntry }) {
  const when = entry.invoicedAt ? new Date(entry.invoicedAt).toLocaleDateString() : "";
  return (
    <span
      title={`Invoiced ${when}${entry.invoiceNumber ? ` · ${entry.invoiceNumber}` : ""}`}
      style={{
        fontSize: 10.5, padding: "2px 6px", borderRadius: 999,
        background: "var(--goodTint)", color: "var(--goodInk)", whiteSpace: "nowrap",
      }}
    >Inv</span>
  );
}

function Figure({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <span style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
      <span style={{ fontSize: 12, color: "var(--muted)" }}>{label}</span>
      <span className="mono" style={{ fontSize: 18, fontWeight: 600, color: muted ? "var(--ink2)" : "var(--ink)" }}>{value}</span>
    </span>
  );
}

/** "Wed · Sep 16" */
function longDayLabel(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return `${date.toLocaleDateString(undefined, { weekday: "short" })} · ${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

/**
 * A spreadsheet, not a report.
 *
 * The amount comes from `rateSnapshot` — the rate as it stood when the entry
 * was logged — so re-exporting last quarter after a rate rise produces the same
 * figures it did at the time.
 */
function entriesToCsv(
  entries: TimeEntry[],
  projects: Project[],
  clients: Client[],
  itemTitle: Map<string, string>,
): string {
  const headers = [
    "Date", "Project", "Client", "Task", "Description",
    "Duration (Hours)", "Duration (Minutes)", "Type", "Rate", "Amount", "Currency",
    "Invoiced", "Invoice #", "Travel %",
  ];
  const q = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const rows = entries
    .slice()
    .sort((a, b) => (a.start || "").localeCompare(b.start || ""))
    .map((e) => {
      const project = projects.find((p) => p.id === e.project);
      const client = clients.find((c) => c.id === e.client);
      const hrs = (e.durationSeconds || 0) / 3600;
      const kind = entryKind(e, projects);
      // Only Billable (hourly) time is charged; the Type column says what the rest was.
      const rate = kind === "hourly" ? (e.rateSnapshot ?? 0) : 0;
      return [
        q(entryDayKey(e)),
        q(project?.name ?? ""),
        q(client?.name ?? project?.client ?? ""),
        q(itemTitle.get(e.item ?? "") ?? ""),
        q(deriveEntryLabel(e.note, itemTitle.get(e.item ?? ""))),
        hrs.toFixed(2),
        String(Math.round((e.durationSeconds || 0) / 60)),
        KIND_LABEL[kind],
        String(rate),
        (hrs * rate).toFixed(2),
        q(e.currency || "USD"),
        q(e.invoicedAt ? toIsoLocal(new Date(e.invoicedAt)) : ""),
        q(e.invoiceNumber ?? ""),
        e.travelPct ? String(e.travelPct) : "",
      ].join(",");
    });
  return [headers.join(","), ...rows].join("\n");
}

function download(filename: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const pagerBtn: React.CSSProperties = {
  width: 36, height: 36, borderRadius: 8, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 15, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
};
const pill: React.CSSProperties = {
  height: 34, padding: "0 13px", borderRadius: 999, fontSize: 12.5, fontWeight: 500, cursor: "pointer",
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)",
  display: "inline-flex", alignItems: "center", gap: 3,
};
const pillOn: React.CSSProperties = {
  background: "var(--accentTint)", border: "1px solid var(--accentLine)", color: "var(--accent)",
};
const head: React.CSSProperties = {
  fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em",
  color: "var(--muted2)",
};

/**
 * The right-hand mark on an uninvoiced row: a check for Billable time waiting
 * on an invoice, and a short word for the kinds that never will be.
 */
function KindMark({ kind }: { kind: EntryKind }) {
  if (kind === "hourly") return <span title="Billable — not invoiced yet" style={{ fontSize: 13, color: "var(--accent)" }}>✓</span>;
  const short: Record<Exclude<EntryKind, "hourly">, string> = { project: "fee", no_charge: "—", internal: "int" };
  return <span title={KIND_LABEL[kind]} style={{ fontSize: 11, color: "var(--muted2)" }}>{short[kind]}</span>;
}
